// Client/src/partner/PartnerClientMaterials.js
import React, { useCallback, useEffect, useMemo, useState } from "react";

const SUPPORT_EMAIL = "support@astoval.com";
const DEFAULT_PRIMARY = "#ff7a00";
const DEFAULT_ACCENT = "#0f766e";

const cardStyle = {
  border: "1px solid #e4e7ec",
  borderRadius: 14,
  background: "#ffffff",
  padding: 20,
  boxShadow: "0 6px 18px rgba(16, 24, 40, 0.04)",
};

const primaryButtonStyle = {
  appearance: "none",
  border: 0,
  borderRadius: 9,
  background: DEFAULT_PRIMARY,
  color: "#111827",
  fontWeight: 850,
  padding: "11px 14px",
  cursor: "pointer",
  lineHeight: 1.2,
};

const secondaryButtonStyle = {
  ...primaryButtonStyle,
  background: "#ffffff",
  border: "1px solid #d0d5dd",
  color: "#344054",
};

const inputStyle = {
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #d0d5dd",
  borderRadius: 9,
  padding: "10px 12px",
  fontSize: 14,
  color: "#101828",
  background: "#ffffff",
};

function cleanText(value, maxLength = 500) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength);
}

function validHexColor(value) {
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(value || "").trim());
}

function safeColor(value, fallback) {
  return validHexColor(value) ? String(value).trim() : fallback;
}

function safeUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    const parsed = new URL(raw, window.location.origin);
    if (["http:", "https:"].includes(parsed.protocol)) return parsed.href;
  } catch {
    return "";
  }

  return "";
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function escapeXml(value) {
  return escapeHtml(value);
}

function normalizeBranding(branding, organizationName = "") {
  const profile =
    branding?.profile && typeof branding.profile === "object" && !Array.isArray(branding.profile)
      ? branding.profile
      : {};

  const partnerName =
    cleanText(profile.partner_name, 120) || cleanText(organizationName, 120) || "TVAC Partner";

  return {
    partnerName,
    logoUrl: safeUrl(profile.logo_url),
    primaryColor: safeColor(profile.primary_color, DEFAULT_PRIMARY),
    accentColor: safeColor(profile.accent_color, DEFAULT_ACCENT),
    contactName: cleanText(profile.contact_name, 120),
    contactEmail: cleanText(profile.contact_email, 254),
    contactPhone: cleanText(profile.contact_phone, 64),
    contactUrl: safeUrl(profile.contact_url),
    contactUrlLabel: cleanText(profile.contact_url_label, 100),
  };
}

function getInitials(name) {
  return (
    cleanText(name, 120)
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "P"
  );
}

function getContactLines(profile) {
  return [
    profile.contactName,
    profile.contactEmail,
    profile.contactPhone,
    profile.contactUrlLabel || profile.contactUrl,
  ].filter(Boolean);
}

function openPrintDocument({ title, bodyHtml, pageCss = "size: A4; margin: 0;" }) {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    throw new Error("Your browser blocked the preview window. Allow pop-ups for this site and try again.");
  }

  try {
    printWindow.opener = null;
  } catch {
    // Best effort only.
  }

  printWindow.document.open();
  printWindow.document.write(`<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(title)}</title>
  <style>
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #eef2f6; color: #101828; font-family: Inter, Arial, Helvetica, sans-serif; }
    @page { ${pageCss} }
    .toolbar { position: sticky; top: 0; z-index: 20; display: flex; justify-content: center; gap: 10px; padding: 12px; background: rgba(255,255,255,.96); border-bottom: 1px solid #d0d5dd; }
    .toolbar button { border: 0; border-radius: 8px; padding: 10px 15px; font-weight: 800; cursor: pointer; background: #ff7a00; color: #111827; }
    .toolbar .secondary { background: #fff; border: 1px solid #d0d5dd; color: #344054; }
    .print-help { margin: 0; align-self: center; color: #475467; font-size: 13px; }
    @media print {
      html, body { background: #fff; }
      .toolbar { display: none !important; }
      .print-page { margin: 0 !important; box-shadow: none !important; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <button type="button" onclick="window.print()">Print / Save as PDF</button>
    <button type="button" class="secondary" onclick="window.close()">Close preview</button>
    <p class="print-help">Choose “Save as PDF” in your browser’s print dialog.</p>
  </div>
  ${bodyHtml}
</body>
</html>`);
  printWindow.document.close();
  printWindow.focus();
}

function createOnePagerHtml(profile) {
  const primary = escapeHtml(profile.primaryColor);
  const accent = escapeHtml(profile.accentColor);
  const partnerName = escapeHtml(profile.partnerName);
  const initials = escapeHtml(getInitials(profile.partnerName));
  const logoUrl = profile.logoUrl ? escapeHtml(profile.logoUrl) : "";
  const contactLines = getContactLines(profile).map(escapeHtml);

  const logoBlock = logoUrl
    ? `<img src="${logoUrl}" alt="${partnerName} logo" style="display:block;max-width:150px;max-height:54px;object-fit:contain;" />`
    : `<div style="width:52px;height:52px;border-radius:12px;display:grid;place-items:center;background:${primary};color:#fff;font-weight:900;font-size:17px;">${initials}</div>`;

  return `
  <main class="print-page" style="width:210mm;min-height:297mm;margin:18px auto;background:#fff;box-shadow:0 14px 35px rgba(16,24,40,.16);position:relative;overflow:hidden;">
    <div style="height:11mm;background:linear-gradient(90deg,${primary},${accent});"></div>
    <div style="padding:15mm 16mm 10mm;">
      <header style="display:flex;align-items:center;justify-content:space-between;gap:18px;">
        <div style="display:flex;align-items:center;gap:13px;min-width:0;">
          ${logoBlock}
          <div>
            <div style="font-size:16px;font-weight:850;line-height:1.25;">${partnerName}</div>
            <div style="margin-top:3px;font-size:10px;letter-spacing:.11em;text-transform:uppercase;color:#667085;font-weight:800;">TVAC Partner</div>
          </div>
        </div>
        <div style="text-align:right;">
          <div style="font-size:18px;font-weight:900;color:#ff7a00;letter-spacing:.05em;">TVAC</div>
          <div style="font-size:10px;color:#667085;">Tokenization Value-Add Calculator</div>
        </div>
      </header>

      <section style="margin-top:13mm;padding:10mm;border-radius:18px;background:linear-gradient(135deg,#0b1720,#173746);color:#fff;position:relative;overflow:hidden;">
        <div style="position:absolute;width:95mm;height:95mm;border-radius:50%;right:-35mm;top:-42mm;background:${primary};opacity:.18;"></div>
        <div style="position:relative;max-width:142mm;">
          <div style="font-size:10px;text-transform:uppercase;letter-spacing:.15em;font-weight:850;color:#fdba74;">Pre-tokenization decision support</div>
          <h1 style="margin:5mm 0 3mm;font-size:29px;line-height:1.08;letter-spacing:-.02em;">Before you tokenize, test the business case.</h1>
          <p style="margin:0;font-size:14px;line-height:1.55;color:#e6edf2;">TVAC provides a structured early-stage assessment of whether tokenization is likely to create sufficient net added value compared with a conventional structure—before substantial resources are committed to legal structuring, platform selection, integration or implementation.</p>
        </div>
      </section>

      <section style="display:grid;grid-template-columns:1.05fr .95fr;gap:9mm;margin-top:10mm;">
        <div>
          <h2 style="margin:0;font-size:17px;">What TVAC examines</h2>
          <div style="margin-top:5mm;display:grid;gap:3.5mm;">
            ${[
              ["New opportunities", "Potential access, utility, distribution or structural benefits."],
              ["Cost savings", "Where tokenization may reduce friction, duplication or operating costs."],
              ["Risk reduction", "Whether the proposed model can improve control, transparency or resilience."],
              ["Tokenization costs", "Technology, governance, legal, operational and integration requirements."],
              ["New risks", "Additional execution, market, regulatory, custody and dependency risks."],
            ]
              .map(
                ([heading, copy]) => `<div style="display:grid;grid-template-columns:7mm 1fr;gap:3mm;align-items:start;">
                  <div style="width:7mm;height:7mm;border-radius:50%;background:${primary};color:#fff;display:grid;place-items:center;font-size:9px;font-weight:900;">✓</div>
                  <div><div style="font-weight:850;font-size:12px;">${heading}</div><div style="margin-top:1mm;color:#475467;font-size:10.5px;line-height:1.42;">${copy}</div></div>
                </div>`
              )
              .join("")}
          </div>
        </div>

        <div style="border:1px solid #e4e7ec;border-radius:14px;padding:7mm;background:#f9fafb;">
          <h2 style="margin:0;font-size:17px;">What the client receives</h2>
          <ul style="margin:5mm 0 0;padding-left:5mm;color:#344054;font-size:11px;line-height:1.6;">
            <li>A clear overall assessment: Viable, Conditional Go or Non-viable</li>
            <li>The principal value drivers, costs and new risks</li>
            <li>Critical assumptions and evidence gaps</li>
            <li>Decision gates that must be resolved before proceeding</li>
            <li>A practical basis for discussion with legal, regulatory, tax, technology and market specialists</li>
          </ul>
          <div style="margin-top:6mm;padding:5mm;border-radius:11px;background:#fff;border-left:4px solid ${accent};">
            <div style="font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:#667085;font-weight:850;">The objective</div>
            <p style="margin:2mm 0 0;font-size:11.5px;line-height:1.5;font-weight:700;">Decide early whether to proceed, redesign the case or stop—before avoidable cost and complexity accumulate.</p>
          </div>
        </div>
      </section>

      <section style="margin-top:10mm;">
        <h2 style="margin:0;font-size:17px;">Where TVAC fits</h2>
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:3mm;margin-top:4mm;align-items:stretch;">
          ${[
            ["1", "Initial idea", "Define the asset, objective and proposed tokenization model."],
            ["2", "TVAC assessment", "Test the expected net value, readiness and unresolved conditions."],
            ["3", "Decision", "Proceed, redesign or stop on a documented basis."],
            ["4", "Specialist execution", "Only then move into detailed structuring and implementation."],
          ]
            .map(
              ([number, heading, copy]) => `<div style="padding:4.5mm;border:1px solid #e4e7ec;border-radius:11px;position:relative;">
                <div style="width:7mm;height:7mm;border-radius:8px;background:${number === "2" ? primary : "#eef2f6"};color:${number === "2" ? "#fff" : "#344054"};display:grid;place-items:center;font-size:10px;font-weight:900;">${number}</div>
                <div style="margin-top:3mm;font-size:11px;font-weight:850;">${heading}</div>
                <div style="margin-top:1.5mm;font-size:9.5px;line-height:1.4;color:#667085;">${copy}</div>
              </div>`
            )
            .join("")}
        </div>
      </section>

      <footer style="margin-top:11mm;padding-top:5mm;border-top:1px solid #e4e7ec;display:flex;justify-content:space-between;gap:10mm;align-items:flex-start;">
        <div style="max-width:125mm;">
          <div style="font-size:10px;font-weight:850;">Presented by ${partnerName}</div>
          <div style="margin-top:1.5mm;font-size:9px;line-height:1.45;color:#667085;">TVAC methodology and report technology are provided by Astoval. TVAC is a structured decision-support tool. It does not constitute legal, regulatory, tax, investment or technical advice and does not guarantee feasibility, compliance, funding or liquidity.</div>
        </div>
        <div style="text-align:right;font-size:9.5px;line-height:1.5;color:#344054;">
          ${contactLines.length ? contactLines.map((line) => `<div>${line}</div>`).join("") : `<div>Contact ${partnerName}</div>`}
        </div>
      </footer>
    </div>
  </main>`;
}

function createPitchSlideHtml(profile) {
  const primary = escapeHtml(profile.primaryColor);
  const accent = escapeHtml(profile.accentColor);
  const partnerName = escapeHtml(profile.partnerName);
  const initials = escapeHtml(getInitials(profile.partnerName));
  const logoUrl = profile.logoUrl ? escapeHtml(profile.logoUrl) : "";

  const logoBlock = logoUrl
    ? `<img src="${logoUrl}" alt="${partnerName} logo" style="max-width:170px;max-height:58px;object-fit:contain;" />`
    : `<div style="width:58px;height:58px;border-radius:13px;display:grid;place-items:center;background:${primary};color:#fff;font-weight:900;font-size:18px;">${initials}</div>`;

  return `
  <main class="print-page" style="width:13.333in;height:7.5in;margin:18px auto;background:#fff;box-shadow:0 14px 35px rgba(16,24,40,.16);position:relative;overflow:hidden;">
    <div style="position:absolute;inset:0;background:#ffffff;"></div>
    <div style="position:absolute;top:0;right:0;width:52%;height:100%;background:linear-gradient(135deg,#0b1720,#143441);clip-path:polygon(22% 0,100% 0,100% 100%,0 100%);"></div>
    <div style="position:absolute;width:360px;height:360px;border-radius:50%;right:-90px;top:-120px;background:${primary};opacity:.2;"></div>
    <div style="position:relative;height:100%;padding:46px 52px;display:grid;grid-template-columns:1.12fr .88fr;gap:48px;">
      <section style="display:flex;flex-direction:column;">
        <div style="display:flex;align-items:center;gap:14px;">${logoBlock}<div><div style="font-size:18px;font-weight:850;">${partnerName}</div><div style="font-size:11px;color:#667085;text-transform:uppercase;letter-spacing:.1em;font-weight:800;margin-top:3px;">TVAC Partner</div></div></div>
        <div style="margin-top:44px;font-size:12px;text-transform:uppercase;letter-spacing:.14em;font-weight:850;color:${primary};">Pre-tokenization decision support</div>
        <h1 style="font-size:38px;line-height:1.08;letter-spacing:-.025em;margin:13px 0 14px;max-width:650px;">Test whether tokenization creates real added value—before implementation begins.</h1>
        <p style="font-size:17px;line-height:1.52;color:#475467;margin:0;max-width:620px;">TVAC compares expected opportunities, cost savings and risk reduction with tokenization costs and new risks. The result is a structured basis for deciding whether to proceed, redesign or stop.</p>
        <div style="margin-top:auto;display:flex;align-items:center;gap:11px;color:#344054;font-weight:750;font-size:14px;">
          <span>Initial idea</span><span style="color:${primary};">→</span><span style="padding:8px 11px;border-radius:8px;background:${primary};color:#fff;">TVAC assessment</span><span style="color:${primary};">→</span><span>Decision gates</span><span style="color:${primary};">→</span><span>Specialist execution</span>
        </div>
      </section>
      <section style="color:#fff;display:flex;flex-direction:column;padding:18px 0 6px 92px;">
        <div style="font-size:24px;font-weight:900;color:#ff8a1f;letter-spacing:.04em;">TVAC</div>
        <div style="font-size:12px;color:#b7c6cf;margin-top:2px;">Tokenization Value-Add Calculator</div>
        <h2 style="font-size:23px;margin:48px 0 18px;">What TVAC clarifies</h2>
        <div style="display:grid;gap:15px;">
          ${[
            "Where tokenization may create genuine economic or operational value",
            "Which costs, dependencies and new risks may outweigh the benefits",
            "Which assumptions and evidence gaps must be resolved",
            "Whether the case should proceed, be redesigned or be stopped",
          ]
            .map(
              (item) => `<div style="display:grid;grid-template-columns:27px 1fr;gap:12px;align-items:start;"><div style="width:27px;height:27px;border-radius:8px;background:${accent};display:grid;place-items:center;font-weight:900;">✓</div><div style="font-size:15px;line-height:1.42;color:#e6edf2;">${item}</div></div>`
            )
            .join("")}
        </div>
        <div style="margin-top:auto;padding-top:18px;border-top:1px solid rgba(255,255,255,.16);font-size:11px;line-height:1.45;color:#b7c6cf;">Structured decision support. Not legal, regulatory, tax, investment or technical advice. TVAC methodology provided by Astoval.</div>
      </section>
    </div>
  </main>`;
}

function createPitchSlideSvg(profile, embeddedLogoDataUrl = "") {
  const primary = profile.primaryColor;
  const accent = profile.accentColor;
  const partnerName = escapeXml(profile.partnerName);
  const initials = escapeXml(getInitials(profile.partnerName));

  return `<svg xmlns="http://www.w3.org/2000/svg" width="1600" height="900" viewBox="0 0 1600 900">
  <defs>
    <linearGradient id="dark" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#0b1720"/><stop offset="1" stop-color="#143441"/></linearGradient>
  </defs>
  <rect width="1600" height="900" fill="#ffffff"/>
  <polygon points="900,0 1600,0 1600,900 720,900" fill="url(#dark)"/>
  <circle cx="1540" cy="-30" r="250" fill="${primary}" opacity=".2"/>
  ${
    embeddedLogoDataUrl
      ? `<rect x="70" y="62" width="76" height="76" rx="16" fill="#ffffff" stroke="#e4e7ec"/><image href="${escapeXml(embeddedLogoDataUrl)}" x="78" y="70" width="60" height="60" preserveAspectRatio="xMidYMid meet"/>`
      : `<rect x="70" y="62" width="76" height="76" rx="16" fill="${primary}"/><text x="108" y="112" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="25" font-weight="700" fill="#ffffff">${initials}</text>`
  }
  <text x="166" y="91" font-family="Arial, Helvetica, sans-serif" font-size="25" font-weight="700" fill="#101828">${partnerName}</text>
  <text x="166" y="119" font-family="Arial, Helvetica, sans-serif" font-size="15" font-weight="700" letter-spacing="2" fill="#667085">TVAC PARTNER</text>
  <text x="70" y="225" font-family="Arial, Helvetica, sans-serif" font-size="18" font-weight="700" letter-spacing="3" fill="${primary}">PRE-TOKENIZATION DECISION SUPPORT</text>
  <text x="70" y="300" font-family="Arial, Helvetica, sans-serif" font-size="52" font-weight="700" fill="#101828">
    <tspan x="70" dy="0">Test whether tokenization creates</tspan><tspan x="70" dy="62">real added value—before</tspan><tspan x="70" dy="62">implementation begins.</tspan>
  </text>
  <text x="70" y="525" font-family="Arial, Helvetica, sans-serif" font-size="24" fill="#475467">
    <tspan x="70" dy="0">TVAC compares expected opportunities, cost savings and risk reduction</tspan><tspan x="70" dy="38">with tokenization costs and new risks. The result is a structured basis</tspan><tspan x="70" dy="38">for deciding whether to proceed, redesign or stop.</tspan>
  </text>
  <text x="1030" y="100" font-family="Arial, Helvetica, sans-serif" font-size="34" font-weight="700" fill="#ff8a1f">TVAC</text>
  <text x="1030" y="130" font-family="Arial, Helvetica, sans-serif" font-size="16" fill="#b7c6cf">Tokenization Value-Add Calculator</text>
  <text x="1030" y="230" font-family="Arial, Helvetica, sans-serif" font-size="31" font-weight="700" fill="#ffffff">What TVAC clarifies</text>
  ${[
    "Where tokenization may create genuine value",
    "Which costs and new risks may outweigh benefits",
    "Which assumptions and evidence gaps remain",
    "Whether to proceed, redesign or stop",
  ]
    .map(
      (item, index) => `<rect x="1030" y="${285 + index * 105}" width="38" height="38" rx="10" fill="${accent}"/><text x="1049" y="${312 + index * 105}" text-anchor="middle" font-family="Arial" font-size="24" font-weight="700" fill="#ffffff">✓</text><text x="1092" y="${312 + index * 105}" font-family="Arial, Helvetica, sans-serif" font-size="21" fill="#e6edf2">${escapeXml(item)}</text>`
    )
    .join("")}
  <rect x="70" y="735" width="177" height="53" rx="11" fill="#eef2f6"/><text x="158" y="769" text-anchor="middle" font-family="Arial" font-size="18" font-weight="700" fill="#344054">Initial idea</text>
  <text x="267" y="770" font-family="Arial" font-size="25" fill="${primary}">→</text>
  <rect x="300" y="735" width="220" height="53" rx="11" fill="${primary}"/><text x="410" y="769" text-anchor="middle" font-family="Arial" font-size="18" font-weight="700" fill="#ffffff">TVAC assessment</text>
  <text x="540" y="770" font-family="Arial" font-size="25" fill="${primary}">→</text>
  <rect x="573" y="735" width="181" height="53" rx="11" fill="#eef2f6"/><text x="663" y="769" text-anchor="middle" font-family="Arial" font-size="18" font-weight="700" fill="#344054">Decision</text>
  <text x="1030" y="820" font-family="Arial, Helvetica, sans-serif" font-size="15" fill="#b7c6cf">Structured decision support. TVAC methodology provided by Astoval.</text>
</svg>`;
}

function buildEmailTemplate(profile, clientName, projectName) {
  const salutation = cleanText(clientName, 100) ? `Dear ${cleanText(clientName, 100)},` : "Dear [Client name],";
  const projectReference = cleanText(projectName, 160) || "your proposed tokenization project";
  const signer = profile.contactName || "[Your name]";
  const contactLines = [signer, profile.partnerName, profile.contactEmail, profile.contactPhone].filter(Boolean);

  return `${salutation}

At ${profile.partnerName}, we use TVAC to help clients assess whether ${projectReference} is likely to create sufficient net added value compared with a conventional structure—before substantial resources are committed to legal structuring, platform selection, integration or implementation.

TVAC provides a structured assessment of the expected opportunities, cost savings and risk reduction, weighed against tokenization costs and new risks. It also identifies the key assumptions, evidence gaps and decision gates that should be resolved before a project proceeds.

The purpose is not to replace specialist legal, regulatory, tax, technical or investment advice. It is to establish whether the underlying business case is strong enough, and sufficiently mature, to justify moving into those more resource-intensive workstreams.

Would a short discussion about whether a TVAC assessment is relevant for ${projectReference} be useful?

Best regards,
${contactLines.join("\n")}`;
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function downloadTextFile(filename, content, mimeType = "text/plain;charset=utf-8") {
  downloadBlob(filename, new Blob([content], { type: mimeType }));
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Could not read the logo file."));
    reader.readAsDataURL(blob);
  });
}

async function getEmbeddedLogoDataUrl(profile) {
  if (!profile.logoUrl) return "";

  try {
    const response = await fetch(profile.logoUrl, { mode: "cors", cache: "no-store" });
    if (!response.ok) return "";
    const blob = await response.blob();
    if (!String(blob.type || "").startsWith("image/")) return "";
    return await blobToDataUrl(blob);
  } catch {
    return "";
  }
}

async function downloadPitchSlidePng(profile) {
  const embeddedLogoDataUrl = await getEmbeddedLogoDataUrl(profile);
  const svg = createPitchSlideSvg(profile, embeddedLogoDataUrl);
  const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const image = await new Promise((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () => reject(new Error("The branded slide image could not be created."));
      nextImage.src = svgUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = 1600;
    canvas.height = 900;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("The browser could not create the slide image.");
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const pngBlob = await new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("The branded slide image could not be exported."));
      }, "image/png");
    });

    downloadBlob("TVAC-branded-client-pitch-slide.png", pngBlob);
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

function MaterialCard({ eyebrow, title, bestFor, howToUse, customisation, children }) {
  return (
    <article style={{ ...cardStyle, display: "flex", flexDirection: "column", minHeight: 390 }}>
      <div style={{ fontSize: 11, letterSpacing: "0.11em", textTransform: "uppercase", fontWeight: 850, color: "#b54708" }}>
        {eyebrow}
      </div>
      <h3 style={{ margin: "8px 0 0", fontSize: 21, lineHeight: 1.2 }}>{title}</h3>

      <dl style={{ margin: "18px 0 0", display: "grid", gap: 13 }}>
        <div>
          <dt style={{ fontWeight: 850, fontSize: 13, color: "#344054" }}>Best used for</dt>
          <dd style={{ margin: "4px 0 0", color: "#475467", fontSize: 13, lineHeight: 1.5 }}>{bestFor}</dd>
        </div>
        <div>
          <dt style={{ fontWeight: 850, fontSize: 13, color: "#344054" }}>How to use it</dt>
          <dd style={{ margin: "4px 0 0", color: "#475467", fontSize: 13, lineHeight: 1.5 }}>{howToUse}</dd>
        </div>
        <div>
          <dt style={{ fontWeight: 850, fontSize: 13, color: "#344054" }}>Customisation</dt>
          <dd style={{ margin: "4px 0 0", color: "#475467", fontSize: 13, lineHeight: 1.5 }}>{customisation}</dd>
        </div>
      </dl>

      <div style={{ marginTop: "auto", paddingTop: 18 }}>{children}</div>
    </article>
  );
}

function Step({ number, title, text }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "34px minmax(0, 1fr)", gap: 11, alignItems: "start" }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, background: "#fff7ed", border: "1px solid #fed7aa", color: "#b54708", display: "grid", placeItems: "center", fontWeight: 900 }}>
        {number}
      </div>
      <div>
        <div style={{ fontWeight: 850, color: "#101828", fontSize: 14 }}>{title}</div>
        <div style={{ marginTop: 3, color: "#475467", fontSize: 13, lineHeight: 1.45 }}>{text}</div>
      </div>
    </div>
  );
}

function PartnerClientMaterials({ apiBase, session, organizationName }) {
  const [branding, setBranding] = useState(null);
  const [loadingBranding, setLoadingBranding] = useState(true);
  const [brandingError, setBrandingError] = useState("");
  const [clientName, setClientName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [copyNotice, setCopyNotice] = useState("");
  const [actionError, setActionError] = useState("");
  const [toolkitImageOk, setToolkitImageOk] = useState(true);

  const loadBranding = useCallback(async () => {
    if (!session?.access_token) {
      setLoadingBranding(false);
      return;
    }

    setLoadingBranding(true);
    setBrandingError("");

    try {
      const response = await fetch(`${apiBase}/api/partner/workspace/branding`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        cache: "no-store",
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.ok || !payload?.branding) {
        throw new Error(payload?.message || "Brand settings could not be loaded.");
      }
      setBranding(payload.branding);
    } catch (error) {
      setBrandingError(error?.message || "Brand settings could not be loaded.");
    } finally {
      setLoadingBranding(false);
    }
  }, [apiBase, session?.access_token]);

  useEffect(() => {
    void loadBranding();
  }, [loadBranding]);

  useEffect(() => {
    function handleBrandingUpdate(event) {
      if (event?.detail) {
        setBranding(event.detail);
        setBrandingError("");
      } else {
        void loadBranding();
      }
    }

    window.addEventListener("tvac-partner-branding-updated", handleBrandingUpdate);
    return () => window.removeEventListener("tvac-partner-branding-updated", handleBrandingUpdate);
  }, [loadBranding]);

  const profile = useMemo(
    () => normalizeBranding(branding, organizationName),
    [branding, organizationName]
  );
  const emailTemplate = useMemo(
    () => buildEmailTemplate(profile, clientName, projectName),
    [clientName, profile, projectName]
  );

  function runMaterialAction(action) {
    setActionError("");
    setCopyNotice("");
    try {
      action();
    } catch (error) {
      setActionError(error?.message || "The material could not be prepared.");
    }
  }

  async function handleCopyEmail() {
    setActionError("");
    try {
      await copyText(emailTemplate);
      setCopyNotice("Email copied to your clipboard.");
    } catch {
      setActionError("The email could not be copied. You can still select and copy it manually.");
    }
  }

  function handleDownloadEmail() {
    runMaterialAction(() => {
      downloadTextFile("TVAC-client-introduction-email.txt", emailTemplate);
      setCopyNotice("Email template downloaded.");
    });
  }

  async function handleDownloadSvg() {
    setActionError("");
    try {
      const embeddedLogoDataUrl = await getEmbeddedLogoDataUrl(profile);
      downloadTextFile(
        "TVAC-branded-client-pitch-slide.svg",
        createPitchSlideSvg(profile, embeddedLogoDataUrl),
        "image/svg+xml;charset=utf-8"
      );
    } catch (error) {
      setActionError(error?.message || "The branded SVG could not be prepared.");
    }
  }

  async function handleDownloadPng() {
    setActionError("");
    try {
      await downloadPitchSlidePng(profile);
    } catch (error) {
      setActionError(error?.message || "The branded PNG could not be prepared.");
    }
  }

  const publicUrl = process.env.PUBLIC_URL || "";

  return (
    <section
      aria-label="Client sales materials"
      style={{
        marginTop: 24,
        borderRadius: 18,
        border: "1px solid #d7e1e7",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fbfc 100%)",
        overflow: "hidden",
        boxShadow: "0 12px 28px rgba(16, 24, 40, 0.06)",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          alignItems: "stretch",
          minHeight: 300,
        }}
      >
        <div style={{ padding: "clamp(24px, 4vw, 38px)" }}>
          <div style={{ display: "inline-flex", padding: "6px 10px", borderRadius: 999, background: "#fff7ed", border: "1px solid #fed7aa", color: "#b54708", fontSize: 12, fontWeight: 850 }}>
            Client-ready commercial toolkit
          </div>
          <h2 style={{ margin: "15px 0 0", fontSize: "clamp(26px, 3vw, 36px)", lineHeight: 1.08, letterSpacing: "-0.02em" }}>
            For your clients
          </h2>
          <p style={{ margin: "14px 0 0", color: "#475467", fontSize: 15, lineHeight: 1.62, maxWidth: 690 }}>
            Use these materials to introduce TVAC before a client commits significant resources to legal structuring, platform selection, integration or implementation. The objective is to establish whether tokenization appears capable of creating sufficient net added value—and which conditions must be resolved before the project should proceed.
          </p>
          <p style={{ margin: "12px 0 0", color: "#344054", fontSize: 14, lineHeight: 1.55, fontWeight: 700 }}>
            Your current Partner Workspace logo, company name, colours and contact details are applied to the branded versions automatically. TVAC methodology, attribution and standard limitations remain fixed.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 20 }}>
            <a href="#brand-settings" style={{ ...secondaryButtonStyle, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
              Review brand settings
            </a>
            <a href="#new-assessment" style={{ ...primaryButtonStyle, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
              Start a client assessment
            </a>
          </div>
        </div>
        <div style={{ minHeight: 260, background: "linear-gradient(135deg, #08232f 0%, #123b48 100%)", overflow: "hidden", position: "relative" }}>
          {toolkitImageOk ? (
            <img
              src={`${publicUrl}/tvac-partner-toolkit.webp`}
              alt="Abstract illustration of structured assessment and decision support"
              onError={() => setToolkitImageOk(false)}
              style={{ width: "100%", height: "100%", minHeight: 300, display: "block", objectFit: "cover", objectPosition: "center" }}
            />
          ) : (
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, padding: 30, color: "#e6edf2" }}>
              <div style={{ position: "absolute", width: 260, height: 260, borderRadius: "50%", right: -70, top: -70, background: profile.primaryColor, opacity: 0.16 }} />
              <div style={{ position: "absolute", width: 220, height: 220, borderRadius: "50%", left: -80, bottom: -90, background: profile.accentColor, opacity: 0.18 }} />
              <div style={{ position: "relative", height: "100%", display: "grid", placeItems: "center" }}>
                <div style={{ maxWidth: 330 }}>
                  <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.14em", color: "#fdba74", fontWeight: 850 }}>Client materials</div>
                  <div style={{ marginTop: 12, fontSize: 30, lineHeight: 1.12, fontWeight: 900 }}>Co-branded assets for client conversations</div>
                  <div style={{ marginTop: 14, display: "grid", gap: 10, fontSize: 14, color: "#d3dee5" }}>
                    <span>✓ One-pager</span>
                    <span>✓ Introduction email</span>
                    <span>✓ Pitch slide</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: "0 clamp(20px, 3vw, 32px) clamp(24px, 4vw, 38px)" }}>
        <div style={{ ...cardStyle, boxShadow: "none", background: "#ffffff", marginTop: 24 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Recommended client workflow</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 18, marginTop: 18 }}>
            <Step number="1" title="Introduce TVAC" text="Send the one-pager when tokenization is first discussed or after an initial client meeting." />
            <Step number="2" title="Start the conversation" text="Personalise the email with the client and project context; avoid generic mass outreach." />
            <Step number="3" title="Explain the process" text="Use the pitch slide in a proposal, workshop or meeting to show where TVAC fits." />
            <Step number="4" title="Run the assessment" text="Submit the case through this workspace once the client wants a structured evaluation." />
            <Step number="5" title="Review and decide" text="Discuss the value drivers, risks, evidence gaps and decision gates before specialist execution." />
          </div>
        </div>

        {loadingBranding ? (
          <div style={{ marginTop: 16, color: "#667085", fontSize: 13 }}>Loading your co-branding profile…</div>
        ) : null}
        {brandingError ? (
          <div role="alert" style={{ marginTop: 16, padding: 12, borderRadius: 10, background: "#fffaeb", border: "1px solid #fedf89", color: "#b54708", fontSize: 13, lineHeight: 1.5 }}>
            {brandingError} The materials below will use standard TVAC styling until the profile is available. You can refresh it or contact {SUPPORT_EMAIL}.
            <button type="button" onClick={() => void loadBranding()} style={{ ...secondaryButtonStyle, marginLeft: 10, padding: "7px 10px" }}>Refresh</button>
          </div>
        ) : null}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))", gap: 16, marginTop: 20 }}>
          <MaterialCard
            eyebrow="PDF-ready one-pager"
            title="Client introduction one-pager"
            bestFor="A first introduction before a meeting, as a follow-up attachment or as a workshop handout."
            howToUse="Open the branded version, review it and use your browser’s Print / Save as PDF command. A standard TVAC PDF is also available."
            customisation={`Automatically uses ${profile.partnerName}, the uploaded logo, selected colours and partner contact details.`}
          >
            <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
              <button
                type="button"
                style={primaryButtonStyle}
                onClick={() => runMaterialAction(() => openPrintDocument({ title: "TVAC client one-pager", bodyHtml: createOnePagerHtml(profile) }))}
              >
                Create branded PDF
              </button>
              <a
                href={`${publicUrl}/tvac-client-one-pager-standard.pdf`}
                target="_blank"
                rel="noreferrer"
                style={{ ...secondaryButtonStyle, textDecoration: "none", display: "inline-flex", alignItems: "center" }}
              >
                Standard PDF
              </a>
            </div>
          </MaterialCard>

          <MaterialCard
            eyebrow="Copy-and-personalise"
            title="Client introduction email"
            bestFor="Starting a focused conversation with an existing client or a carefully selected prospect."
            howToUse="Add the client and project names, then edit one or two sentences so the relevance is specific rather than generic."
            customisation="Your partner name and saved contact details are inserted automatically. The core TVAC scope and limitations are retained."
          >
            <div style={{ display: "grid", gap: 9 }}>
              <input value={clientName} onChange={(event) => setClientName(event.target.value)} placeholder="Client name (optional)" maxLength={100} style={inputStyle} />
              <input value={projectName} onChange={(event) => setProjectName(event.target.value)} placeholder="Project or asset (optional)" maxLength={160} style={inputStyle} />
              <textarea value={emailTemplate} readOnly rows={8} aria-label="Generated client introduction email" style={{ ...inputStyle, resize: "vertical", lineHeight: 1.45, background: "#f9fafb" }} />
              <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
                <button type="button" style={primaryButtonStyle} onClick={() => void handleCopyEmail()}>Copy email</button>
                <button type="button" style={secondaryButtonStyle} onClick={handleDownloadEmail}>Download .txt</button>
              </div>
            </div>
          </MaterialCard>

          <MaterialCard
            eyebrow="Presentation asset"
            title="TVAC client pitch slide"
            bestFor="Client proposals, presentations and workshops where you need to explain TVAC’s place in the decision process."
            howToUse="Open the branded slide and save it as a landscape PDF, or download the branded SVG and insert it into your own deck."
            customisation="The branded versions use your partner name and colours. The editable PowerPoint template remains deliberately generic."
          >
            <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
              <button
                type="button"
                style={primaryButtonStyle}
                onClick={() => runMaterialAction(() => openPrintDocument({ title: "TVAC client pitch slide", bodyHtml: createPitchSlideHtml(profile), pageCss: "size: 13.333in 7.5in; margin: 0;" }))}
              >
                Create branded slide
              </button>
              <button type="button" style={secondaryButtonStyle} onClick={() => void handleDownloadPng()}>Download PNG</button>
              <button type="button" style={secondaryButtonStyle} onClick={() => void handleDownloadSvg()}>Download SVG</button>
              <a
                href={`${publicUrl}/tvac-client-pitch-slide-template.pptx`}
                style={{ ...secondaryButtonStyle, textDecoration: "none", display: "inline-flex", alignItems: "center" }}
              >
                PowerPoint template
              </a>
            </div>
          </MaterialCard>
        </div>

        {copyNotice ? <div role="status" style={{ marginTop: 14, color: "#027a48", fontWeight: 700, fontSize: 13 }}>{copyNotice}</div> : null}
        {actionError ? <div role="alert" style={{ marginTop: 14, color: "#b42318", fontWeight: 700, fontSize: 13, lineHeight: 1.5 }}>{actionError}</div> : null}

        <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
          <div style={{ padding: 16, borderRadius: 12, background: "#ecfdf3", border: "1px solid #abefc6" }}>
            <strong style={{ color: "#05603a" }}>Partners may personalise</strong>
            <p style={{ margin: "7px 0 0", color: "#344054", fontSize: 13, lineHeight: 1.5 }}>
              Company logo and colours, contact details, client and project context, the description of the partner’s own services, and the proposed call to action.
            </p>
          </div>
          <div style={{ padding: 16, borderRadius: 12, background: "#fffaeb", border: "1px solid #fedf89" }}>
            <strong style={{ color: "#b54708" }}>Core statements remain fixed</strong>
            <p style={{ margin: "7px 0 0", color: "#344054", fontSize: 13, lineHeight: 1.5 }}>
              Do not alter TVAC’s methodology, scope, outcome labels, limitations or attribution, or imply guaranteed compliance, feasibility, funding, investor demand or liquidity.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default PartnerClientMaterials;
